import { useFormikContext } from "formik";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AddressQcBanner } from "@/features/clients/components/address-qc-banner";
import type { Client } from "@/features/clients/types";
import {
  FormikCell,
  FormikCheckbox,
  FormikRadioYN,
  FormikSsnField,
} from "@/features/forms/components/formik-controls";
import type { Form8379Data } from "@/features/forms/types";
import { NoPractitionerBanner } from "./no-practitioner-banner";

/**
 * Part II — Injured-spouse information (legacy `isa_part2.php`). The two
 * taxpayers exactly as shown on the joint return (names, SSNs, which is the
 * injured spouse), the refund-in-both-names question (Q11), and the
 * different-refund-address question (Q12) revealing a conditional address (§9).
 * Shows the incomplete-address and no-practitioner nudges.
 */
export function IsaPart2({ client, onNext }: { client: Client; onNext: () => void }) {
  const { values } = useFormikContext<Form8379Data>();
  const showDiffAddress = values.sp_ref_add_diff === "Y";

  return (
    <div className="flex flex-col gap-6">
      <AddressQcBanner warnings={client.addressQc ?? []} />
      <NoPractitionerBanner clientId={client.id} practitioner={client.practitioner} />

      <Card className="gap-0 overflow-hidden overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name (as shown on the return)</TableHead>
              <TableHead className="w-44">SSN</TableHead>
              <TableHead className="w-32">Injured spouse</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                <FormikCell name="sp_name1" aria-label="First taxpayer name" maxLength={100} />
              </TableCell>
              <TableCell>
                <FormikSsnField name="sp_ssn1" aria-label="First taxpayer SSN" />
              </TableCell>
              <TableCell>
                <FormikCheckbox name="sp_injure_chk" aria-label="First taxpayer is the injured spouse" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <FormikCell name="sp_name2" aria-label="Second taxpayer name" maxLength={100} />
              </TableCell>
              <TableCell>
                <FormikSsnField name="sp_ssn2" aria-label="Second taxpayer SSN" />
              </TableCell>
              <TableCell>
                <FormikCheckbox name="sp_injure_chk2" aria-label="Second taxpayer is the injured spouse" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      <Card className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">11</span>
            Was the refund issued in both spouses' names?
          </span>
          <FormikCheckbox name="ref_both_name" label="Yes, issued in both names" />
        </div>
        <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">12</span>
            Mail the injured-spouse refund to a different address?
          </span>
          <FormikRadioYN name="sp_ref_add_diff" aria-label="Mail refund to a different address" />
        </div>
        {showDiffAddress && (
          <div className="grid gap-2 border-t pt-4">
            <Label htmlFor="sp_diff_add">Number / street / city / state / ZIP</Label>
            <FormikCell name="sp_diff_add" maxLength={100} />
          </div>
        )}
        <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <span className="text-foreground text-sm">Is the injured spouse self-employed?</span>
          <FormikCheckbox name="self_emp" label="Yes, self-employed" />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onNext}>
          Go to Part III <ArrowRight />
        </Button>
      </div>
    </div>
  );
}
